import React from 'react';
import shortid from 'shortid';
import classnames from 'classnames';
import { noop, isEmpty } from 'lodash';

// local dependencies
import { Icon } from 'modules/icon-bank';
import { buttonPreloader as ButtonPreloader } from 'ui/views/generic/buttonPreloader';
import { computeSize } from 'utils/computeSize';
import { SIZES } from 'constants/common';
import { getLabel } from 'utils/getLabel';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { button as Button } from 'modules/button';
 *
 * // render
 * <Button propName='propValue'/>
 */
export const button = ( props ) => {

	// get actual size constant
	const size = computeSize( props.size );

	// button class name
	const buttonClassName = classnames( 'ui-button', props.className, {
		[ `ui-button--${ size }` ]: ! isEmpty( size ),
		[ `ui-button--theme-${ props.theme }` ]: ! isEmpty( props.theme ),
		'ui-button--hoverable': props.hover,
		'ui-button--disabled': props.disabled || props.loading,
		'ui-button--outline': props.outline,
	} );

	// icon class name
	const buttonIconClassName = classnames( 'ui-button__icon', props.buttonIconClassName, {
		[ `ui-button__icon--${ size }` ]: ! isEmpty( size ),
	} );

	// post icon class name
	const buttonPostIconClassName = classnames( 'ui-button__iconPost', props.buttonIconClassName, {
		[ `ui-button__iconPost--${ size }` ]: ! isEmpty( size ),
		'ui-button__iconPost--with-left-margin': props.postIconMargin
	} );

	// text (label) class name
	const buttonTextClassName = classnames( 'ui-button__text', props.buttonTextClassName, {
		[ `ui-button__text--${ size }` ]: ! isEmpty( size ),
	} );

    return (
        <button
			id = { props.id }
			type = { props.type }
			style = { {
				padding: props.padding,
			} }
			className = { buttonClassName }
			onClick = { props.onClick }
			disabled = { props.disabled || props.loading }
			data-action-name={ props.name }
		>

			{/* loading */}
			{
				props.loading ? (
					<ButtonPreloader radius={ props.radius } />
				) : ( null )
			}

			{/* pre icon */}
			{
				props.showIcon && ( !isEmpty( props.iconId ) ) && ( !props.loading ) ? (
					<span className = { buttonIconClassName }>
						<Icon id={ props.iconId } width={ props.iconWidth } height={ props.iconHeight } fill={ props.iconFill }/>
					</span>
				): null
			}

			{/* title */}
			{
				props.showTitle && ( !props.loading ) ? (
					<span className={ buttonTextClassName }>
						{ getLabel( props.title || props.label ) }
					</span>
				): null
			}

			{/* post icon */}
			{
				props.showPostIcon && ( !isEmpty( props.postIconId ) ) && ( !props.loading ) ? (
					<span className = { buttonPostIconClassName }>
						<Icon id={ props.postIconId } width={ props.iconWidth } height={ props.iconHeight } fill={ props.iconFill }/>
					</span>
				): null
			}
		</button>
    );
};

// set display name
button.displayName = 'button';

// set default props
button.defaultProps = {
	id: shortid.generate(),
    type: 'button',
	label: {
		ref: '',
		value: 'Button'
	},
	iconId: '',
	postIconId: '',
	disabled: false,
	size: SIZES.MEDIUM,
	padding: undefined,
	hover: false,
	outline: false,
	onClick: noop,
	loading: false,
	showIcon: false,
	showPostIcon: false,
	showTitle: true,
	iconWidth: undefined,
	iconHeight: undefined,
	theme: undefined,
	iconFill: undefined,
};

// prop types
button.propTypes = {};


